/**
 * Invoices of the project: start one from the quote (or from scratch), fill in the buyer and the
 * lines, check it, issue it — the invoice is then kept in the project, numbered from the series
 * kept on this device, and never changed. Each issued invoice downloads as a PDF (with a SEPA
 * payment QR code) and as a Peppol BIS Billing 3.0 file for e-invoicing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  localeTag,
  newId,
  type Invoice,
  type InvoiceLine,
  type InvoiceParty,
  type Op,
  type Project,
  type VatRegime,
} from '@elec/domain';
import type { Locale, MessageKey, Translate } from '@elec/i18n';
import { loadPriceList, type Platform } from '@elec/platform';
import type { CloudClient } from '@elec/sync';
import type * as Exporters from '@elec/export';
import { availableSources, loadServerPrices, ordered, preferredOf, useReferenceOf } from './priceSources.ts';
import { Dialog } from './ui.tsx';

type X = typeof Exporters;

/** The installer's invoicing details and invoice series, kept on this device. */
export interface SellerProfile {
  party: Invoice['seller'];
  /** Series prefix, e.g. "2026-". */
  prefix: string;
  /** Next number in the series. */
  next: number;
  /** Payment term (days). */
  termDays: number;
}

const PREF = 'invoiceSeller';
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, d: number) => {
  const t = new Date(`${iso}T12:00:00`);
  t.setDate(t.getDate() + d);
  return t.toISOString().slice(0, 10);
};

function loadProfile(platform: Platform, project: Project): SellerProfile {
  const year = new Date().getFullYear();
  const blank: SellerProfile = {
    party: {
      name: project.meta.company,
      street: '',
      postcode: '',
      city: '',
      country: 'BE',
      vatNumber: '',
      peppolId: '',
      email: '',
      iban: '',
      bic: '',
    },
    prefix: `${year}-`,
    next: 1,
    termDays: 30,
  };
  try {
    const v = JSON.parse(platform.getPref(PREF) ?? 'null') as SellerProfile | null;
    if (!v) return blank;
    // A new year starts a new series when the prefix is the year.
    if (/^\d{4}-$/.test(v.prefix) && Number(v.prefix.slice(0, 4)) < year)
      return { ...v, prefix: `${year}-`, next: 1 };
    return { ...blank, ...v, party: { ...blank.party, ...v.party } };
  } catch {
    return blank;
  }
}

const UNITS = ['C62', 'MTR', 'HUR'] as const;

function PartyFields(props: {
  t: Translate;
  value: InvoiceParty;
  onChange(p: InvoiceParty): void;
  id: string;
  bank?: { iban: string; bic: string; onChange(b: { iban: string; bic: string }): void };
}) {
  const { t, value: p } = props;
  const field = (
    k: keyof InvoiceParty,
    label: MessageKey,
    extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {},
  ) => (
    <div className="field">
      <label htmlFor={`${props.id}-${k}`}>{t(label)}</label>
      <input
        id={`${props.id}-${k}`}
        value={p[k]}
        onChange={(e) => props.onChange({ ...p, [k]: e.target.value })}
        data-testid={`${props.id}-${k}`}
        {...extra}
      />
    </div>
  );
  return (
    <div className="form-grid">
      {field('name', 'invoice.name')}
      {field('vatNumber', 'invoice.vatNumber', { placeholder: 'BE0123456789' })}
      {field('street', 'invoice.street')}
      <div className="field-row">
        {field('postcode', 'invoice.postcode')}
        {field('city', 'invoice.city')}
        {field('country', 'invoice.country', { maxLength: 2, style: { width: 48 } })}
      </div>
      {field('email', 'invoice.email', { type: 'email' })}
      {field('peppolId', 'invoice.peppolId', { placeholder: '0208:0123456789' })}
      {props.bank && (
        <>
          <div className="field">
            <label htmlFor={`${props.id}-iban`}>IBAN</label>
            <input
              id={`${props.id}-iban`}
              value={props.bank.iban}
              onChange={(e) => props.bank!.onChange({ iban: e.target.value, bic: props.bank!.bic })}
              data-testid={`${props.id}-iban`}
            />
          </div>
          <div className="field">
            <label htmlFor={`${props.id}-bic`}>BIC</label>
            <input
              id={`${props.id}-bic`}
              value={props.bank.bic}
              onChange={(e) => props.bank!.onChange({ iban: props.bank!.iban, bic: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  );
}

export function InvoiceDialog(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  platform: Platform;
  baseName: string;
  readOnly: boolean;
  load(): Promise<X>;
  save(bytes: Uint8Array, name: string, ext: string, mime: string): Promise<void>;
  dispatch(label: string, ops: Op[]): void;
  notify(msg: string): void;
  /** Start a new invoice from this quote. */
  fromQuote?: Exporters.Quote | undefined;
  /** Signed-in account: the wholesalers' price lists kept on the server. */
  client?: CloudClient | null;
  onClose(): void;
}) {
  const { t, project, locale } = props;
  const [x, setX] = useState<X | null>(null);
  const [profile, setProfile] = useState(() => loadProfile(props.platform, project));
  const [draft, setDraft] = useState<Invoice | null>(null);
  const issued = useMemo(
    () =>
      Object.values(project.invoices ?? {}).sort(
        (a, b) => b.issueDate.localeCompare(a.issueDate) || b.number.localeCompare(a.number),
      ),
    [project.invoices],
  );
  const money = (n: number) => n.toLocaleString(localeTag(locale), { style: 'currency', currency: 'EUR' });

  const newDraft = (ex: X, lines: InvoiceLine[], vat: VatRegime): Invoice => {
    const number = ex.invoiceNumber(profile.prefix, profile.next);
    const issueDate = today();
    return {
      id: newId(),
      number,
      issueDate,
      dueDate: addDays(issueDate, profile.termDays),
      seller: profile.party,
      buyer: ex.buyerFromProject(project),
      lines,
      vat,
      paymentRef: ex.structuredReference(number.replace(/\D/g, '')),
      orderRef: '',
      note: '',
    };
  };
  useEffect(() => {
    void props.load().then((ex) => {
      setX(ex);
      if (props.fromQuote)
        setDraft(newDraft(ex, ex.linesFromQuote(props.fromQuote), ex.vatOfQuote(props.fromQuote)));
    });
  }, []);

  const startFromQuote = async () => {
    if (!x || !draft) return;
    // Same prices as the quote: the preferred source first, then the others.
    const [own, server] = await Promise.all([
      loadPriceList(),
      loadServerPrices(x, project, props.client ?? null),
    ]);
    const sources = ordered(
      availableSources(x, own, server.data, useReferenceOf(props.platform), ''),
      preferredOf(props.platform),
    );
    const quote = x.buildQuote(project, { sources: sources.map((s) => ({ id: s.id, entries: s.entries })) });
    setDraft({ ...draft, lines: [...draft.lines, ...x.linesFromQuote(quote)] });
  };
  // Stable row keys (lines are plain data): an edited line keeps its row, a removed one takes its own.
  const keys = useRef(new WeakMap<InvoiceLine, string>());
  const keyOf = (l: InvoiceLine) => {
    let k = keys.current.get(l);
    if (!k) keys.current.set(l, (k = newId()));
    return k;
  };
  const setLine = (i: number, l: Partial<InvoiceLine>) =>
    draft &&
    setDraft({
      ...draft,
      lines: draft.lines.map((o, j) => {
        if (j !== i) return o;
        const n = { ...o, ...l };
        keys.current.set(n, keyOf(o));
        return n;
      }),
    });
  const num = (s: string) => {
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const download = async (inv: Invoice, kind: 'pdf' | 'xml') => {
    if (!x) return;
    const name = `${t('invoice.file')} ${inv.number}`.replace(/[\\/:*?"<>|]+/g, '-');
    try {
      if (kind === 'pdf')
        await props.save(await x.invoicePdf(inv, locale), `${name}.pdf`, 'pdf', 'application/pdf');
      else
        await props.save(
          new TextEncoder().encode(x.invoiceUbl(inv, locale)),
          `${name} - Peppol.xml`,
          'xml',
          'application/xml',
        );
    } catch (e) {
      props.notify(t('error.export', { msg: (e as Error).message }));
    }
  };

  const issue = () => {
    if (!x || !draft) return;
    const inv: Invoice = {
      ...draft,
      seller: { ...draft.seller, vatNumber: x.formatVat(draft.seller.vatNumber) },
      buyer: {
        ...draft.buyer,
        vatNumber: draft.buyer.vatNumber.trim() ? x.formatVat(draft.buyer.vatNumber) : '',
      },
    };
    if (issued.some((o) => o.number === inv.number))
      return props.notify(t('invoice.duplicate', { n: inv.number }));
    props.dispatch(t('invoice.txIssue', { n: inv.number }), [{ op: 'add', coll: 'invoices', value: inv }]);
    // The series moves on past the number used (also when it was typed by hand).
    const used = Number(/(\d+)$/.exec(inv.number)?.[1] ?? profile.next);
    const next: SellerProfile = { ...profile, party: inv.seller, next: Math.max(profile.next, used + 1) };
    setProfile(next);
    props.platform.setPref(PREF, JSON.stringify(next));
    setDraft(null);
  };

  const problems = draft && x ? x.invoiceProblems(draft) : [];
  const totals = draft && x ? x.invoiceTotals(draft) : null;
  const peppol = draft && x ? x.peppolReady(draft) : false;

  return (
    <Dialog
      title={draft ? t('invoice.new') : t('invoice.title')}
      wide
      onClose={props.onClose}
      actions={
        draft ? (
          <>
            <button onClick={() => setDraft(null)}>{t('dialog.cancel')}</button>
            <button
              className="primary"
              disabled={problems.length > 0 || props.readOnly}
              onClick={issue}
              data-testid="invoice-issue"
            >
              {t('invoice.issue')}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => x && setDraft(newDraft(x, [], 'S21'))}
              disabled={!x || props.readOnly}
              data-testid="invoice-new"
            >
              {t('invoice.new')}
            </button>
            <button className="primary" onClick={props.onClose}>
              {t('dialog.close')}
            </button>
          </>
        )
      }
    >
      {!draft ? (
        <>
          <p className="muted">{t('invoice.intro')}</p>
          {issued.length === 0 ? (
            <p className="muted" data-testid="invoice-none">
              {t('invoice.none')}
            </p>
          ) : (
            <div className="quote-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('invoice.number')}</th>
                    <th>{t('invoice.issueDate')}</th>
                    <th>{t('invoice.buyer')}</th>
                    <th className="num">{t('invoice.total')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody data-testid="invoice-list">
                  {issued.map((inv) => (
                    <tr key={inv.id}>
                      <td className="mono">{inv.number}</td>
                      <td>{new Date(`${inv.issueDate}T12:00:00`).toLocaleDateString(localeTag(locale))}</td>
                      <td>{inv.buyer.name}</td>
                      <td className="num">{x ? money(x.invoiceTotals(inv).total) : ''}</td>
                      <td className="inv-actions">
                        <button onClick={() => void download(inv, 'pdf')} data-testid="invoice-pdf">
                          PDF
                        </button>
                        <button
                          onClick={() => void download(inv, 'xml')}
                          disabled={!x?.peppolReady(inv)}
                          title={x?.peppolReady(inv) ? t('invoice.peppolHint') : t('invoice.peppolMissing')}
                          data-testid="invoice-ubl"
                        >
                          Peppol (UBL)
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="inv-edit" data-testid="invoice-editor">
          <details className="inv-section" open={!profile.party.vatNumber}>
            <summary>
              <strong>{t('invoice.seller')}</strong> — {draft.seller.name || '…'}{' '}
              <span className="muted small">{t('invoice.sellerKept')}</span>
            </summary>
            <PartyFields
              t={t}
              id="inv-seller"
              value={draft.seller}
              onChange={(p) => setDraft({ ...draft, seller: { ...draft.seller, ...p } })}
              bank={{
                iban: draft.seller.iban,
                bic: draft.seller.bic,
                onChange: (b) => setDraft({ ...draft, seller: { ...draft.seller, ...b } }),
              }}
            />
          </details>
          <h4>{t('invoice.buyer')}</h4>
          <PartyFields
            t={t}
            id="inv-buyer"
            value={draft.buyer}
            onChange={(buyer) => setDraft({ ...draft, buyer })}
          />
          <p className="muted small">{t('invoice.buyerHint')}</p>
          <h4>{t('invoice.details')}</h4>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="inv-number">{t('invoice.number')}</label>
              <input
                id="inv-number"
                value={draft.number}
                onChange={(e) => setDraft({ ...draft, number: e.target.value })}
                data-testid="inv-number"
              />
            </div>
            <div className="field">
              <label htmlFor="inv-vat">{t('invoice.vat')}</label>
              <select
                id="inv-vat"
                value={draft.vat}
                onChange={(e) => setDraft({ ...draft, vat: e.target.value as VatRegime })}
                data-testid="inv-vat"
              >
                <option value="S21">{t('quote.vat21')}</option>
                <option value="S6">{t('quote.vat6')}</option>
                <option value="AE">{t('invoice.reverseCharge')}</option>
              </select>
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="inv-date">{t('invoice.issueDate')}</label>
                <input
                  id="inv-date"
                  type="date"
                  value={draft.issueDate}
                  onChange={(e) =>
                    e.target.value &&
                    setDraft({
                      ...draft,
                      issueDate: e.target.value,
                      dueDate: addDays(e.target.value, profile.termDays),
                    })
                  }
                />
              </div>
              <div className="field">
                <label htmlFor="inv-due">{t('invoice.dueDate')}</label>
                <input
                  id="inv-due"
                  type="date"
                  value={draft.dueDate}
                  onChange={(e) => e.target.value && setDraft({ ...draft, dueDate: e.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="inv-order">{t('invoice.orderRef')}</label>
              <input
                id="inv-order"
                value={draft.orderRef}
                onChange={(e) => setDraft({ ...draft, orderRef: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="inv-ogm">{t('invoice.paymentRef')}</label>
              <input
                id="inv-ogm"
                className="mono"
                value={draft.paymentRef}
                onChange={(e) => setDraft({ ...draft, paymentRef: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="inv-note">{t('invoice.note')}</label>
              <input
                id="inv-note"
                value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              />
            </div>
          </div>
          {x && draft.vat !== 'S21' && <p className="muted small">{x.vatMention(draft.vat, locale)}</p>}

          <div className="inv-lines-head">
            <h4>{t('invoice.lines')}</h4>
            <span className="grow" />
            <button onClick={() => void startFromQuote()} data-testid="invoice-from-quote">
              {t('invoice.addQuoteLines')}
            </button>
            <button
              onClick={() =>
                setDraft({
                  ...draft,
                  lines: [
                    ...draft.lines,
                    { description: '', reference: '', quantity: 1, unitCode: 'C62', unitPrice: 0 },
                  ],
                })
              }
              data-testid="invoice-add-line"
            >
              + {t('invoice.addLine')}
            </button>
          </div>
          <div className="quote-table inv-lines">
            <table>
              <thead>
                <tr>
                  <th>{t('quote.colDesc')}</th>
                  <th>{t('quote.colRef')}</th>
                  <th className="num">{t('quote.colQty')}</th>
                  <th>{t('invoice.unit')}</th>
                  <th className="num">{t('quote.colPrice')}</th>
                  <th className="num">{t('invoice.amount')}</th>
                  <th />
                </tr>
              </thead>
              <tbody data-testid="invoice-lines">
                {draft.lines.map((l, i) => (
                  <tr key={keyOf(l)} className={l.unitPrice ? '' : 'unpriced'}>
                    <td>
                      <input
                        value={l.description}
                        onChange={(e) => setLine(i, { description: e.target.value })}
                        aria-label={t('quote.colDesc')}
                      />
                    </td>
                    <td>
                      <input
                        className="mono"
                        value={l.reference}
                        onChange={(e) => setLine(i, { reference: e.target.value })}
                        aria-label={t('quote.colRef')}
                      />
                    </td>
                    <td className="num">
                      <input
                        inputMode="decimal"
                        defaultValue={String(l.quantity).replace('.', locale === 'en' ? '.' : ',')}
                        onBlur={(e) => setLine(i, { quantity: num(e.target.value) })}
                        aria-label={t('quote.colQty')}
                      />
                    </td>
                    <td>
                      <select
                        value={l.unitCode}
                        onChange={(e) => setLine(i, { unitCode: e.target.value })}
                        aria-label={t('invoice.unit')}
                      >
                        {UNITS.map((u) => (
                          <option key={u} value={u}>
                            {t(`invoice.unit.${u}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        inputMode="decimal"
                        defaultValue={
                          l.unitPrice ? String(l.unitPrice).replace('.', locale === 'en' ? '.' : ',') : ''
                        }
                        onBlur={(e) => setLine(i, { unitPrice: Math.round(num(e.target.value) * 100) / 100 })}
                        aria-label={t('quote.colPrice')}
                        data-testid="invoice-line-price"
                      />
                    </td>
                    <td className="num">{x ? money(x.lineAmount(l)) : ''}</td>
                    <td>
                      <button
                        className="icon-btn"
                        aria-label={t('tx.delete')}
                        onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totals && (
            <p className="inv-totals" data-testid="invoice-totals">
              {t('quote.totals', {
                sub: money(totals.net),
                vat: money(totals.vat),
                total: money(totals.total),
              })}
            </p>
          )}
          {problems.length > 0 ? (
            <ul className="warn inv-problems" data-testid="invoice-problems">
              {problems.map((p) => (
                <li key={p}>{t(`invoice.problem.${p}` as MessageKey)}</li>
              ))}
            </ul>
          ) : (
            <p className={peppol ? 'ok-text' : 'muted'} data-testid="invoice-peppol">
              {peppol ? t('invoice.peppolReady') : t('invoice.pdfOnly')}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
